/**
 * TailorPanel -- AI "Tailor for target role" drawer (ADR-071).
 *
 * Drawer flow:
 *   1. Form:  target_role (required, 1..200) + optional job_description (<=8000 chars)
 *   2. Loading state with aria-live announcement.
 *   3. Diff view: patches grouped by target (personal_info first, then one
 *      collapsible per career_entry), each with a checkbox for accept/reject,
 *      side-by-side current vs. suggested, "Select all" / "Clear" controls.
 *   4. Accept Selected / Accept All buttons.
 *   5. Inline soft-error area (ADR-055) with code-specific messaging; 409
 *      stale-version renders a "Re-run tailor" action.
 *
 * Never renders raw AI output via dangerouslySetInnerHTML. All suggested
 * strings are rendered as plain text; whitespace is sanitized (collapsed
 * runs of control chars, trimmed) before display.
 *
 * Accessibility:
 *   - role="dialog", aria-modal, labelled by the heading.
 *   - Esc closes the drawer.
 *   - Initial focus lands on the target_role input; focus is trapped inside.
 *   - aria-live="polite" region announces loading/error/ready status.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Button } from '../ui/Button';
import {
  useTailorStore,
  useTailorActions,
  useSelectedPatchIds,
} from '../../state/cms/useTailorStore';
import { useSiteActions } from '../../state/cms/useSiteStore';
import type {
  TailorCareerEntryPatch,
  TailorErrorCode,
  TailorPatch,
  TailorPersonalInfoPatch,
} from '../../services/tailorSiteApi';

const JOB_DESCRIPTION_MAX = 8000;
const TARGET_ROLE_MAX = 200;

interface TailorPanelProps {
  siteId: string;
  open: boolean;
  onClose: () => void;
  /** Optional toast hook. Called with a success message after apply. */
  onApplied?: (message: string) => void;
}

/** Collapse control chars / excessive whitespace without mutating content. */
function sanitizeText(input: string | null | undefined): string {
  if (input == null) return '';
  /* eslint-disable-next-line no-control-regex */
  return input.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').trim();
}

/** Human-friendly labels for ADR-055 soft-fail codes. */
function errorCodeMessage(code: TailorErrorCode | null): string | null {
  switch (code) {
    case 'ai_failed':
      return 'The AI service could not generate a suggestion. Please try again in a moment.';
    case 'parse_failed':
      return 'The AI returned an unreadable response. Please try again.';
    case 'schema_failed':
      return 'The AI returned an unexpected shape. Please try again.';
    case 'empty_after_filter':
      return 'The AI produced no valid suggestions for this role. Try a more specific target role or add a job description.';
    case 'stale_version':
      return 'This site was edited in another tab while you were reviewing. Re-run the tailor to work against the latest version.';
    case 'quota_exceeded':
      return 'You have reached the hourly tailor limit. Please try again later.';
    case 'cooldown':
      return 'Please wait a few seconds before requesting another tailor.';
    case 'not_found':
      return 'This site is no longer available.';
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'network_error':
      return 'Network error. Check your connection and try again.';
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function PersonalInfoPatchRow({
  patch,
  selected,
  onToggle,
}: {
  patch: TailorPersonalInfoPatch;
  selected: boolean;
  onToggle: () => void;
}) {
  const checkboxId = `tailor-patch-${patch.patch_id}`;
  return (
    <div className="tailor-panel__patch">
      <div className="tailor-panel__patch-head">
        <input
          id={checkboxId}
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Accept change to ${patch.field_name}`}
        />
        <label htmlFor={checkboxId} className="tailor-panel__patch-title">
          {patch.field_name}
        </label>
      </div>
      <div className="tailor-panel__diff">
        <div className="tailor-panel__diff-col tailor-panel__diff-col--current">
          <div className="tailor-panel__diff-label">Current</div>
          <div className="tailor-panel__diff-text">
            {sanitizeText(patch.current_value) || <em>(empty)</em>}
          </div>
        </div>
        <div className="tailor-panel__diff-col tailor-panel__diff-col--suggested">
          <div className="tailor-panel__diff-label">Suggested</div>
          <div className="tailor-panel__diff-text">
            {sanitizeText(patch.value)}
          </div>
        </div>
      </div>
    </div>
  );
}

function CareerEntryPatchRow({
  patch,
  selected,
  onToggle,
}: {
  patch: TailorCareerEntryPatch;
  selected: boolean;
  onToggle: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const checkboxId = `tailor-patch-${patch.patch_id}`;
  const title = sanitizeText(patch.label) || 'Career entry';

  return (
    <div className="tailor-panel__patch">
      <div className="tailor-panel__patch-head">
        <input
          id={checkboxId}
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Accept changes to ${title}`}
        />
        <label htmlFor={checkboxId} className="tailor-panel__patch-title">
          {title}
        </label>
        <button
          type="button"
          className="tailor-panel__collapse"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse diff' : 'Expand diff'}
        >
          {expanded ? '▾' : '▸'}
        </button>
      </div>
      {expanded && (
        <>
          {(patch.current_summary != null || patch.summary != null) && (
            <div className="tailor-panel__diff">
              <div className="tailor-panel__diff-col tailor-panel__diff-col--current">
                <div className="tailor-panel__diff-label">Current summary</div>
                <div className="tailor-panel__diff-text">
                  {sanitizeText(patch.current_summary) || <em>(empty)</em>}
                </div>
              </div>
              <div className="tailor-panel__diff-col tailor-panel__diff-col--suggested">
                <div className="tailor-panel__diff-label">Suggested summary</div>
                <div className="tailor-panel__diff-text">
                  {sanitizeText(patch.summary) || <em>(empty)</em>}
                </div>
              </div>
            </div>
          )}
          <div className="tailor-panel__diff">
            <div className="tailor-panel__diff-col tailor-panel__diff-col--current">
              <div className="tailor-panel__diff-label">Current responsibilities</div>
              <ul className="tailor-panel__bullets">
                {patch.current_responsibilities.length === 0 ? (
                  <li>
                    <em>(none)</em>
                  </li>
                ) : (
                  patch.current_responsibilities.map((r, i) => (
                    <li key={i}>{sanitizeText(r)}</li>
                  ))
                )}
              </ul>
            </div>
            <div className="tailor-panel__diff-col tailor-panel__diff-col--suggested">
              <div className="tailor-panel__diff-label">Suggested responsibilities</div>
              <ul className="tailor-panel__bullets">
                {patch.responsibilities.length === 0 ? (
                  <li>
                    <em>(none)</em>
                  </li>
                ) : (
                  patch.responsibilities.map((r, i) => (
                    <li key={i}>{sanitizeText(r)}</li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function TailorPanel({
  siteId,
  open,
  onClose,
  onApplied,
}: TailorPanelProps) {
  const status = useTailorStore((s) => s.status);
  const suggestion = useTailorStore((s) => s.suggestion);
  const error = useTailorStore((s) => s.error);
  const errorCode = useTailorStore((s) => s.errorCode);
  const lastRequest = useTailorStore((s) => s.lastRequest);
  const selectedIds = useSelectedPatchIds();
  const { runTailor, togglePatch, selectAll, clearSelection, applySelected, clear } =
    useTailorActions();
  const { fetchSections } = useSiteActions();

  const [targetRole, setTargetRole] = useState('');
  const [jobDescription, setJobDescription] = useState('');

  const drawerRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  /* ---- Lifecycle: focus management + Esc close + scroll lock ---- */
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    /* Focus the first field on mount. */
    setTimeout(() => firstFieldRef.current?.focus(), 0);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  /* ---- Reset store when drawer closes ---- */
  useEffect(() => {
    if (!open) {
      clear();
      setTargetRole('');
      setJobDescription('');
    }
  }, [open, clear]);

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        /* Simple focus trap: cycle through focusable elements. */
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const trimmed = targetRole.trim();
      if (trimmed.length === 0 || trimmed.length > TARGET_ROLE_MAX) return;
      void runTailor(siteId, {
        target_role: trimmed,
        job_description: jobDescription.trim() || undefined,
      });
    },
    [targetRole, jobDescription, runTailor, siteId],
  );

  const handleApply = useCallback(
    async (acceptAll: boolean) => {
      if (acceptAll) selectAll();
      /* If acceptAll, the store update is synchronous via immer, so the
         next line sees the refreshed selectedPatchMap. */
      const ok = await applySelected(siteId);
      if (ok) {
        /* Refetch site sections to pick up variant/override changes. */
        void fetchSections(siteId);
        onApplied?.('Tailored changes applied.');
        onClose();
      }
    },
    [selectAll, applySelected, siteId, fetchSections, onApplied, onClose],
  );

  /* ---- Grouping ---- */
  const { personalPatches, careerPatches } = useMemo(() => {
    const personal: TailorPersonalInfoPatch[] = [];
    const career: TailorCareerEntryPatch[] = [];
    for (const p of (suggestion?.patches ?? []) as TailorPatch[]) {
      if (p.target === 'personal_info') personal.push(p);
      else career.push(p);
    }
    return { personalPatches: personal, careerPatches: career };
  }, [suggestion]);

  if (!open) return null;

  const softErrorMessage = errorCodeMessage(errorCode);
  const showStaleRerun = errorCode === 'stale_version' && lastRequest != null;
  const liveMessage =
    status === 'loading'
      ? 'Generating tailoring suggestions...'
      : status === 'applying'
        ? 'Applying selected changes...'
        : status === 'error'
          ? `Error: ${softErrorMessage ?? error ?? 'Unknown error'}`
          : status === 'ready'
            ? `Ready. ${suggestion?.patches.length ?? 0} suggestions available.`
            : '';

  return (
    <div
      className="tailor-panel__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={drawerRef}
        className="tailor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tailor-panel-title"
        onKeyDown={handleKeyDown}
      >
        <div className="tailor-panel__header">
          <h2 id="tailor-panel-title" className="tailor-panel__title">
            Tailor for target role
          </h2>
          <button
            type="button"
            className="tailor-panel__close"
            onClick={onClose}
            aria-label="Close tailor panel"
          >
            ×
          </button>
        </div>

        <div className="tailor-panel__body">
          <div
            className="tailor-panel__live"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {liveMessage}
          </div>

          {status === 'idle' || status === 'loading' || status === 'error' ? (
            <form
              className="tailor-panel__form"
              onSubmit={handleSubmit}
              aria-label="Tailor for target role form"
            >
              <div className="input-wrapper">
                <label htmlFor="tailor-target-role" className="input-label">
                  Target role
                  <span aria-hidden="true"> *</span>
                </label>
                <input
                  id="tailor-target-role"
                  ref={firstFieldRef}
                  className="input"
                  type="text"
                  maxLength={TARGET_ROLE_MAX}
                  required
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  placeholder="e.g. Senior Platform Engineer"
                  aria-required="true"
                  aria-describedby="tailor-target-role-hint"
                  disabled={status === 'loading'}
                />
                <div
                  id="tailor-target-role-hint"
                  className="tailor-panel__hint"
                >
                  {targetRole.length}/{TARGET_ROLE_MAX}
                </div>
              </div>

              <div className="input-wrapper">
                <label htmlFor="tailor-job-description" className="input-label">
                  Job description (optional)
                </label>
                <textarea
                  id="tailor-job-description"
                  className="input tailor-panel__textarea"
                  maxLength={JOB_DESCRIPTION_MAX}
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Paste the full job description to sharpen the tailoring."
                  rows={6}
                  disabled={status === 'loading'}
                />
                <div className="tailor-panel__hint">
                  {jobDescription.length}/{JOB_DESCRIPTION_MAX}
                </div>
              </div>

              {status === 'error' && (softErrorMessage || error) && (
                <div
                  className="tailor-panel__error"
                  role="alert"
                  data-code={errorCode ?? 'unknown'}
                >
                  <p>{softErrorMessage ?? error}</p>
                  {showStaleRerun && lastRequest && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => void runTailor(siteId, lastRequest)}
                    >
                      Re-run tailor
                    </Button>
                  )}
                </div>
              )}

              <div className="tailor-panel__actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={status === 'loading'}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={status === 'loading'}
                  disabled={
                    status === 'loading' ||
                    targetRole.trim().length === 0 ||
                    targetRole.trim().length > TARGET_ROLE_MAX
                  }
                >
                  Generate suggestions
                </Button>
              </div>
            </form>
          ) : null}

          {status === 'ready' && suggestion && (
            <div className="tailor-panel__diff-view">
              <div className="tailor-panel__toolbar">
                <span className="tailor-panel__toolbar-summary">
                  {selectedIds.size} of {suggestion.patches.length} selected
                </span>
                <div className="tailor-panel__toolbar-actions">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={selectAll}
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={clearSelection}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {personalPatches.length > 0 && (
                <section className="tailor-panel__group">
                  <h3 className="tailor-panel__group-title">
                    Personal info overrides
                  </h3>
                  {personalPatches.map((p) => (
                    <PersonalInfoPatchRow
                      key={p.patch_id}
                      patch={p}
                      selected={selectedIds.has(p.patch_id)}
                      onToggle={() => togglePatch(p.patch_id)}
                    />
                  ))}
                </section>
              )}

              {careerPatches.length > 0 && (
                <section className="tailor-panel__group">
                  <h3 className="tailor-panel__group-title">Career entry variants</h3>
                  {careerPatches.map((p) => (
                    <CareerEntryPatchRow
                      key={p.patch_id}
                      patch={p}
                      selected={selectedIds.has(p.patch_id)}
                      onToggle={() => togglePatch(p.patch_id)}
                    />
                  ))}
                </section>
              )}

              {personalPatches.length === 0 && careerPatches.length === 0 && (
                <p className="tailor-panel__empty">
                  The AI produced no suggestions for this role.
                </p>
              )}

              <div className="tailor-panel__actions">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void handleApply(true)}
                  disabled={suggestion.patches.length === 0}
                >
                  Accept all
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => void handleApply(false)}
                  disabled={selectedIds.size === 0}
                >
                  Accept selected
                </Button>
              </div>
            </div>
          )}

          {status === 'applying' && (
            <div className="tailor-panel__applying">
              <p>Applying selected changes...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
